export const CompetenceType = {
    Education: 'education',
    Practice: 'practice',
    Certificate: 'certificate',
    Permit: 'permit',
    License: 'license',
    Group: 'group',
};

// `help` visas som hjälptext efter namnet i typväljaren (FormSelect)
export const competenceTypeOptions = [
    { index: CompetenceType.Education, name: 'Utbildning', help: '(Gymnasium, YH eller universitet)' },
    { index: CompetenceType.Practice, name: 'Praktik', help: '(LIA, APL eller praktik)' },
    { index: CompetenceType.Certificate, name: 'Certifikat', help: '(Intern- eller branschutbildning)' },
    { index: CompetenceType.Permit, name: 'Tillstånd', help: '(Utfärdat av arbetsgivaren)' },
    { index: CompetenceType.License, name: 'Licens', help: '(Utfärdad av myndighet)' },
    { index: CompetenceType.Group, name: 'Kompetensgrupp', help: '(Flera kompetenser som utfärdas tillsammans)' },
];

// Utfärdanderegler per kompetenstyp — speglar Competence::ISSUANCE_RULES i backend.
// Typer som saknas här får DEFAULT_ISSUANCE_RULES.
//   courseInfo: visa Kursinformation (kursstart/-slut, timmar) vid utfärdande
//   authority:  null, eller etiketter för utfärdare + referenskod och om båda är obligatoriska
const ISSUANCE_RULES = {
    [CompetenceType.License]: {
        courseInfo: false,
        authority: {
            authorityLabel: 'Utfärdande myndighet',
            authorityPlaceholder: 'T.ex. Transportstyrelsen',
            referenceLabel: 'Myndighetens referenskod',
            referencePlaceholder: 'Fyll i myndighetens referenskod',
            required: true,
        },
    },
    [CompetenceType.Permit]: {
        courseInfo: false,
        authority: {
            authorityLabel: 'Utfärdande organisation eller person',
            authorityPlaceholder: 'T.ex. arbetsgivaren',
            referenceLabel: 'Referenskod',
            referencePlaceholder: 'Fyll i referenskod',
            required: false,
        },
    },
    // Gruppen utfärdas aldrig själv, så den bär varken kursinformation eller
    // myndighetsfält — de reglerna kommer från respektive vald medlemskompetens.
    [CompetenceType.Group]: { courseInfo: false, authority: null },
};

const DEFAULT_ISSUANCE_RULES = { courseInfo: true, authority: null };

export const issuanceRules = (type) => ISSUANCE_RULES[type] ?? DEFAULT_ISSUANCE_RULES;

// Etikett med asterisk när fältet är obligatoriskt
export const requiredMark = (label, required) => (required ? `${label}*` : label);

// Vuelidate-regel för FormInput: alltid en sträng så att fältet får ett v$-objekt
export const requiredValidator = (required) => (required ? 'required' : '');

export const requiresIssuingAuthority = (type) => issuanceRules(type).authority !== null;

// En kompetensgrupp utfärdas aldrig själv: certifikat ställs ut på de valda medlemskompetenserna
export const isGroup = (type) => type === CompetenceType.Group;

// A group and its members belong together in the competence selection: the ids a
// row drags along when it is selected, in both directions.
export const groupRelatedIds = (competence) => [
    ...(competence.groups ?? []).map((group) => group.id),
    ...(competence.members ?? []).map((member) => member.id),
];

// Issuance offers the group, never the competences inside one of the listed groups
export const issuanceOptions = (competences) => {
    const grouped = new Set(competences.flatMap((competence) => (competence.members ?? []).map((member) => member.id)));

    return competences
        .filter((competence) => !grouped.has(competence.id))
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
};

// En huvudkompetens och en kompetensgrupp utfärdas aldrig direkt
export const isIssuable = (competence) => !competence.is_main && !isGroup(competence.competence_type);

// Raderna som API:t tar emot i `competences[]`. GroupMemberIssuanceTable bär även
// presentationsfält (name, expires_label) som aldrig ska följa med i anropet.
export const groupRowsToApi = (rows) =>
    (rows ?? []).map(
        ({
            competence_id,
            expires_at,
            custom_expire_date,
            issuing_authority,
            authority_reference,
            sub_competences,
        }) => ({
            competence_id,
            expires_at,
            custom_expire_date,
            issuing_authority,
            authority_reference,
            ...(sub_competences?.length && { sub_competences }),
        })
    );

// Educate departments issue neither tillstånd nor licenser (Competence::EMPLOY_ONLY_TYPES)
export const EMPLOY_ONLY_TYPES = [CompetenceType.Permit, CompetenceType.License];

export const educateCompetenceTypeOptions = competenceTypeOptions.filter(
    (option) => !EMPLOY_ONLY_TYPES.includes(option.index)
);

export const authorityField = (type) => {
    const authority = issuanceRules(type).authority;

    return authority
        ? {
              label: requiredMark(authority.authorityLabel, authority.required),
              placeholder: authority.authorityPlaceholder,
              validators: requiredValidator(authority.required),
          }
        : null;
};

export const competenceTypeLabel = (type) =>
    competenceTypeOptions.find((option) => option.index === type)?.name ?? type;

// Etikett före utfärdandedatumet i kompetenslistor: tillstånd och licenser
// utfärdas (av arbetsgivare/myndighet), övriga typer genomförs
export const issuanceLabel = (type) => (requiresIssuingAuthority(type) ? 'Utfärdat' : 'Genomförd');
